// ─────────────────────────────────────────────────────────────
//  Payment-Engine (Frontend-Fassade)
//
//  Die deterministische Abrechnungs-/Steuerlogik (Beträge, Brutto/Netto,
//  Fälligkeiten) lebt zentral in ./billingCore.js – der EINZIGEN QUELLE
//  DER WAHRHEIT. Dieses Modul re-exportiert sie für das Frontend und
//  ergänzt rein anzeige-/Frontend-spezifische Helfer (Formatierung,
//  Länder-UI, Domain-Erkennung, Provisions-Helfer).
//
//  Die Cloud Function (functions/index.js) nutzt EXAKT dieselbe Core-Datei
//  über eine automatische Kopie (siehe firebase.json predeploy-Hook) –
//  damit können Cron und Dashboard nicht mehr auseinanderdriften.
//
//  >>> Beträge/Steuerlogik NUR in src/Admin/billingCore.js ändern. <<<
// ─────────────────────────────────────────────────────────────

import core from './billingCore';

// ── Re-Export der geteilten Core-Logik (Single Source of Truth) ──
export const DEFAULT_TAX_MODE = core.DEFAULT_TAX_MODE;
export const DEFAULT_VAT_RATE = core.DEFAULT_VAT_RATE;
export const TAX_MODES = core.TAX_MODES;
export const taxModeInfo = core.taxModeInfo;
export const effectiveVatRate = core.effectiveVatRate;
export const taxBreakdown = core.taxBreakdown;
export const grossFromNet = core.grossFromNet;
export const SELLER_COUNTRY = core.SELLER_COUNTRY;
export const EU_COUNTRIES = core.EU_COUNTRIES;
export const COUNTRY_NAMES = core.COUNTRY_NAMES;
export const countryName = core.countryName;
export const isEuCountry = core.isEuCountry;
export const taxModeForCountry = core.taxModeForCountry;
export const paymentId = core.paymentId;
export const monthsElapsed = core.monthsElapsed;
export const billingPeriods = core.billingPeriods;
export const nextDuePeriod = core.nextDuePeriod;
export const contractRunning = core.contractRunning;
export const memberMonthlyPayout = core.memberMonthlyPayout;
export const payoutRemainingMonths = core.payoutRemainingMonths;
export const payoutId = core.payoutId;
export const payoutPeriods = core.payoutPeriods;
export const payoutForPaidInvoice = core.payoutForPaidInvoice;
export const dueDateForOffset = core.dueDateForOffset;

// ── Einmalzahlungen (Setup-Fee, Beratung, …) ──
export const ONE_TIME_PREFIX = core.ONE_TIME_PREFIX;
export const ONE_TIME_COMMISSION_MODES = core.ONE_TIME_COMMISSION_MODES;
export const oneTimePeriodKey = core.oneTimePeriodKey;
export const isOneTimePeriodKey = core.isOneTimePeriodKey;
export const oneTimeIdFromPeriodKey = core.oneTimeIdFromPeriodKey;
export const normalizeOneTime = core.normalizeOneTime;
export const oneTimeList = core.oneTimeList;
export const findOneTime = core.findOneTime;
export const oneTimePeriods = core.oneTimePeriods;
export const oneTimeCommissionFor = core.oneTimeCommissionFor;
export const oneTimePayoutTotal = core.oneTimePayoutTotal;
export const payoutForPaidOneTime = core.payoutForPaidOneTime;
export const INCLUDED_APPTS_PER_MONTH = core.INCLUDED_APPTS_PER_MONTH;
export const REPORTING_WINDOW_DAYS = core.REPORTING_WINDOW_DAYS;
export const reportingId = core.reportingId;
export const includedAppointmentsFor = core.includedAppointmentsFor;
export const appointmentsOnPayment = core.appointmentsOnPayment;
export const hasRecurringBilling = core.hasRecurringBilling;
export const extraFromQualified = core.extraFromQualified;
export const qualifiedFromExtra = core.qualifiedFromExtra;
export const reportingPeriods = core.reportingPeriods;
export const reportingState = core.reportingState;

// ── Provisionsmodell (BonusClub-Staffel, commissionCore.js) ──
export const COMMISSION_ROLES = core.COMMISSION_ROLES;
export const COMMISSION_ROLE_IDS = core.COMMISSION_ROLE_IDS;
export const COMMISSION_PLAN_BASE = core.COMMISSION_PLAN_BASE;
export const commissionRoleInfo = core.commissionRoleInfo;
export const commissionActive = core.commissionActive;
export const commissionBaseFor = core.commissionBaseFor;
export const appointmentPriceFor = core.appointmentPriceFor;
export const commissionRoleForMember = core.commissionRoleForMember;
export const customerRoleOverride = core.customerRoleOverride;
export const memberCommissionMinimum = core.memberCommissionMinimum;
export const commissionForRole = core.commissionForRole;
export const commissionForMember = core.commissionForMember;
export const commissionBreakdown = core.commissionBreakdown;

// ── Frontend-spezifische Helfer (nicht in der Cloud Function genutzt) ──

export const fmtEur = (n) =>
  !isFinite(n) ? '–' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' €';

// Reihenfolge für die Länderauswahl in der UI (Inland zuerst, dann EU, dann Drittland).
export const COUNTRY_OPTIONS = [
  'ES', 'DE', 'AT', 'FR', 'IT', 'NL', 'BE', 'PT', 'IE', 'PL', 'SE', 'DK', 'FI',
  'GR', 'CZ', 'SK', 'HU', 'RO', 'BG', 'HR', 'SI', 'LT', 'LV', 'EE', 'LU', 'MT', 'CY',
  'OTHER_EU', 'CH', 'GB', 'US', 'CA', 'AU', 'OTHER_NON_EU',
];

// ccTLD -> ISO-Land. Generische TLDs (.com/.io/.net …) liefern null -> Land muss
// manuell bestätigt werden.
const TLD_COUNTRY = {
  es: 'ES', de: 'DE', at: 'AT', ch: 'CH', fr: 'FR', it: 'IT', nl: 'NL', be: 'BE',
  pt: 'PT', ie: 'IE', pl: 'PL', se: 'SE', dk: 'DK', fi: 'FI', gr: 'GR', cz: 'CZ',
  sk: 'SK', hu: 'HU', ro: 'RO', bg: 'BG', hr: 'HR', si: 'SI', lt: 'LT', lv: 'LV',
  ee: 'EE', lu: 'LU', mt: 'MT', cy: 'CY', us: 'US', ca: 'CA',
};

// Land automatisch aus der Website/Domain ableiten (anhand der Länder-TLD).
export function countryFromWebsite(website) {
  if (!website || typeof website !== 'string') return null;
  let host = website.trim().toLowerCase();
  host = host.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].split('?')[0].split(':')[0];
  if (!host || !host.includes('.')) return null;
  if (host.endsWith('.co.uk') || host.endsWith('.uk')) return 'GB';
  const tld = host.split('.').pop();
  return TLD_COUNTRY[tld] || null;
}

export const monthLabel = (periodKey) => {
  if (!periodKey || periodKey === 'setup') return 'Setup';
  // Einmalzahlungen tragen ihre Bezeichnung auf dem Datensatz (`label`); ohne
  // ihn bleibt nur der generische Fallback.
  if (core.isOneTimePeriodKey(periodKey)) return 'Einmalzahlung';
  const [y, m] = String(periodKey).split('-').map(Number);
  if (!y || !m) return periodKey;
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

export const fmtDate = (ms) =>
  !ms ? '–' : new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

// memberMonthlyPayout / payoutRemainingMonths leben jetzt in billingCore.js
// (Single Source of Truth) und werden oben re-exportiert.
